import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  MapPin,
  Car,
  Clock,
  Bus,
  Utensils,
  Accessibility,
  Navigation,
  ExternalLink,
  ParkingCircle,
  Compass,
  Info,
  Plane,
  ChevronDown,
} from "lucide-react";
import { PhotoWatermark } from "../components/PhotoElements";

const ADDRESS_LINE_1 = "1604 2nd Avenue North";
const ADDRESS_LINE_2 = "Birmingham, AL 35203";
const MAPS_QUERY = encodeURIComponent(
  "Encore Theatre and Gallery, 1604 2nd Avenue North, Birmingham, AL 35203"
);
const MAPS_LINK = `https://www.google.com/maps/dir/?api=1&destination=${MAPS_QUERY}`;
const MAPS_EMBED = `https://www.google.com/maps?q=${MAPS_QUERY}&output=embed`;

const ROUTES = [
  {
    id: "airport",
    origin: "Birmingham-Shuttlesworth Intl. Airport",
    short: "BHM",
    drive: "10–15 min",
    distance: "≈ 6 mi",
    icon: Plane,
    steps: [
      "Exit the airport via Messer Airport Highway and follow signs toward I-20 West / I-59 South — Downtown Birmingham / Tuscaloosa.",
      "Merge onto I-20 West / I-59 South toward Downtown Birmingham.",
      "Take Exit 126A — Carraway Boulevard / US-31 / US-280.",
      "Coming off the exit, continue toward downtown and follow 6th Avenue North west.",
      "Turn left onto 16th Street North and continue toward 2nd Avenue North.",
      "Turn left onto 2nd Avenue North — you're there.",
    ],
    tip: "The airport is close to downtown, but allow additional time during weekday rush hour or major downtown events.",
  },
  {
    id: "huntsville",
    origin: "Huntsville, Alabama",
    short: "HSV",
    drive: "1 hr 30 – 1 hr 45",
    distance: "≈ 100 mi",
    icon: Compass,
    steps: [
      "Take I-565 West from Huntsville toward Decatur / Birmingham.",
      "Merge onto I-65 South toward Birmingham and continue south into the metro area.",
      "As you reach downtown, take Exit 261A toward I-20 East / I-59 North — Atlanta / Gadsden.",
      "Once on I-20/59, take the first downtown exit for 17th Street North.",
      "Continue straight on 17th Street North into downtown.",
      "Turn left onto 2nd Avenue North.",
      "Continue one block to 16th Street North — Encore is on your left.",
    ],
    tip: "Once you reach the I-20/59 and I-65 interchange, you're only minutes from Encore. Watch the overhead signs carefully — downtown exits come quickly.",
  },
  {
    id: "atlanta",
    origin: "Atlanta, Georgia",
    short: "ATL",
    drive: "2 hr 15 – 2 hr 30",
    distance: "≈ 150 mi",
    icon: Compass,
    steps: [
      "Take I-20 West from Atlanta toward Birmingham.",
      "As you enter Birmingham, I-20 West joins I-59 South.",
      "Continue on I-20 West / I-59 South toward Downtown Birmingham.",
      "Take Exit 126A — Carraway Boulevard / US-31 / US-280.",
      "Continue straight from the exit onto 6th Avenue North toward downtown.",
      "Follow 6th Avenue North to 16th Street North and turn left.",
      "Continue south to 2nd Avenue North and turn left — you've arrived.",
    ],
    tip: null,
    callout: {
      title: "A note for our Atlanta guests",
      body: "Birmingham is in the Central Time Zone; Atlanta is Eastern — Birmingham is one hour behind. A 7:30 PM curtain in Birmingham is 8:30 PM Atlanta time. Plan your trip using Birmingham local time and add cushion for interstate traffic.",
    },
  },
  {
    id: "montgomery",
    origin: "Montgomery, Alabama",
    short: "MGM",
    drive: "1 hr 25 – 1 hr 40",
    distance: "≈ 90 mi",
    icon: Compass,
    steps: [
      "Take I-65 North from Montgomery toward Birmingham.",
      "Remain on I-65 North as you travel through the Birmingham metro area.",
      "As you approach downtown, take Exit 260B — 3rd Avenue North / Downtown.",
      "At the bottom of the ramp, turn right onto 3rd Avenue North.",
      "Continue east on 3rd Avenue North toward downtown.",
      "Turn right onto 16th Street North.",
      "Travel one block and turn left onto 2nd Avenue North.",
    ],
    tip: "Once you reach the 3rd Avenue North exit, Encore is only a few blocks away.",
  },
];

function SectionHeading({ eyebrow, title, icon: Icon }) {
  return (
    <div className="mb-10 md:mb-14 border-b border-[#DE9E48]/15 pb-6">
      <p className="font-overline text-[0.7rem] tracking-[0.3em] text-[#DE9E48] mb-3 inline-flex items-center gap-2">
        <Icon size={14} strokeWidth={1.75} />
        {eyebrow}
      </p>
      <h2 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[1.02] text-white">
        {title}
      </h2>
    </div>
  );
}

function InfoCard({ icon: Icon, title, children, tone = "default" }) {
  const bg =
    tone === "highlight"
      ? "bg-gradient-to-br from-[#563727]/60 via-[#3A2C25]/60 to-[#372C2E]/60"
      : "bg-[#3A2C25]/40";
  return (
    <div
      className={`group relative border border-white/8 hover:border-[#DE9E48]/40 ${bg} backdrop-blur-sm p-6 md:p-8 transition-all duration-500`}
    >
      <span
        aria-hidden
        className="pointer-events-none absolute top-0 left-0 h-[2px] w-8 bg-[#DE9E48] transition-all duration-500 group-hover:w-24"
      />
      <div className="flex items-start gap-4 mb-4">
        <div className="w-11 h-11 rounded-full border border-[#DE9E48]/30 flex items-center justify-center bg-[#211a1c]">
          <Icon size={18} strokeWidth={1.75} className="text-[#DE9E48]" />
        </div>
        <h3 className="font-display text-2xl md:text-[1.5rem] leading-tight tracking-tight text-white pt-1">
          {title}
        </h3>
      </div>
      <div className="font-body text-[0.95rem] leading-relaxed text-stone-300 space-y-3 pl-15">
        {children}
      </div>
    </div>
  );
}

function ParkingStep({ n, title, meta, children }) {
  return (
    <li className="relative border border-white/8 hover:border-[#DE9E48]/40 bg-[#3A2C25]/40 hover:bg-[#3A2C25]/70 p-6 md:p-7 transition-all duration-500 group">
      <div className="flex items-start gap-5">
        <div
          aria-hidden
          className="flex-shrink-0 w-12 h-12 md:w-14 md:h-14 rounded-full border border-[#DE9E48]/40 flex items-center justify-center bg-gradient-to-br from-[#7A431D] to-[#563727]"
        >
          <span className="font-display text-xl md:text-2xl text-white tabular-nums">
            {String(n).padStart(2, "0")}
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 mb-2">
            <h4 className="font-display text-xl md:text-2xl tracking-tight text-white">
              {title}
            </h4>
            {meta && (
              <span className="font-overline text-[0.6rem] tracking-[0.24em] text-[#DE9E48]">
                {meta}
              </span>
            )}
          </div>
          <div className="font-body text-[0.95rem] leading-relaxed text-stone-300 space-y-2">
            {children}
          </div>
        </div>
      </div>
    </li>
  );
}

function RouteCard({ route, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const Icon = route.icon;
  return (
    <article
      data-testid={`route-card-${route.id}`}
      className={
        "group relative border transition-all duration-500 " +
        (open
          ? "border-[#DE9E48]/50 bg-[#3A2C25]/70"
          : "border-white/8 hover:border-[#DE9E48]/40 bg-[#3A2C25]/40 hover:bg-[#3A2C25]/60")
      }
    >
      <span
        aria-hidden
        className={
          "pointer-events-none absolute top-0 left-0 h-[2px] bg-[#DE9E48] transition-all duration-500 " +
          (open ? "w-32" : "w-8 group-hover:w-24")
        }
      />

      <button
        type="button"
        data-testid={`route-toggle-${route.id}`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full text-left p-6 md:p-8 flex items-center gap-5 md:gap-7"
      >
        {/* Origin badge */}
        <div
          aria-hidden
          className="flex-shrink-0 w-14 h-14 md:w-16 md:h-16 rounded-full border border-[#DE9E48]/40 flex items-center justify-center bg-gradient-to-br from-[#563727] to-[#372C2E]"
        >
          <Icon size={22} strokeWidth={1.75} className="text-[#DE9E48]" />
        </div>

        <div className="flex-1 min-w-0">
          <p className="font-overline text-[0.6rem] tracking-[0.28em] text-[#DE9E48] mb-1.5">
            — From {route.short}
          </p>
          <h3 className="font-display text-xl md:text-2xl leading-tight tracking-tight text-white">
            {route.origin}
          </h3>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mt-2 font-overline text-[0.62rem] tracking-[0.22em] text-stone-400">
            <span className="inline-flex items-center gap-1.5">
              <Clock size={11} strokeWidth={2} className="text-[#DE9E48]" />
              {route.drive}
            </span>
            <span className="tabular-nums">{route.distance}</span>
          </div>
        </div>

        <div
          aria-hidden
          className={
            "flex-shrink-0 w-9 h-9 rounded-full border border-[#DE9E48]/30 flex items-center justify-center text-[#DE9E48] transition-transform duration-500 " +
            (open ? "rotate-180 bg-[#7A431D]/30" : "")
          }
        >
          <ChevronDown size={16} />
        </div>
      </button>

      {open && (
        <div className="px-6 md:px-8 pb-8 pt-2 md:pl-[7.5rem]">
          {/* Timeline steps */}
          <ol className="relative border-l border-[#DE9E48]/25 ml-3 space-y-4">
            {route.steps.map((step, i) => (
              <li key={i} className="relative pl-6 md:pl-8">
                <span
                  aria-hidden
                  className="absolute -left-[9px] top-1.5 w-4 h-4 rounded-full border border-[#DE9E48]/60 bg-[#211a1c] flex items-center justify-center"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-[#DE9E48]" />
                </span>
                <p className="font-overline text-[0.58rem] tracking-[0.28em] text-[#DE9E48] mb-1 tabular-nums">
                  Step {String(i + 1).padStart(2, "0")}
                </p>
                <p className="font-body text-[0.95rem] leading-relaxed text-stone-200">
                  {step}
                </p>
              </li>
            ))}
            <li className="relative pl-6 md:pl-8">
              <span
                aria-hidden
                className="absolute -left-[11px] top-1 w-5 h-5 rounded-full bg-[#7A431D] border border-[#DE9E48] flex items-center justify-center"
              >
                <MapPin size={11} className="text-white" strokeWidth={2.5} />
              </span>
              <p className="font-overline text-[0.58rem] tracking-[0.28em] text-[#DE9E48] mb-1">
                Arrive
              </p>
              <p className="font-display text-lg md:text-xl text-white tracking-tight leading-tight">
                Encore Theatre &amp; Gallery
              </p>
              <p className="font-body text-sm text-stone-400 mt-1">
                {ADDRESS_LINE_1} · {ADDRESS_LINE_2}
              </p>
            </li>
          </ol>

          {route.tip && (
            <div className="mt-6 border-l-2 border-[#DE9E48]/60 bg-[#372C2E]/60 pl-5 py-3.5 flex items-start gap-3">
              <Info
                size={14}
                strokeWidth={2}
                className="text-[#DE9E48] flex-shrink-0 mt-1"
              />
              <p className="font-body italic text-sm text-stone-200 leading-relaxed">
                <span className="not-italic font-overline text-[0.6rem] tracking-[0.24em] text-[#DE9E48] mr-2">
                  Travel tip
                </span>
                {route.tip}
              </p>
            </div>
          )}

          {route.callout && (
            <div className="mt-6 border border-[#DE9E48]/40 bg-gradient-to-br from-[#7A431D]/20 via-[#563727]/20 to-transparent p-5 md:p-6">
              <p className="font-overline text-[0.62rem] tracking-[0.28em] text-[#DE9E48] mb-2 inline-flex items-center gap-2">
                <Clock size={12} strokeWidth={2} />
                {route.callout.title}
              </p>
              <p className="font-body text-[0.95rem] leading-relaxed text-stone-200">
                {route.callout.body}
              </p>
            </div>
          )}
        </div>
      )}
    </article>
  );
}

export default function Directions() {
  return (
    <div
      data-testid="directions-page"
      className="pt-28 pb-24 bg-[#211a1c] text-stone-300 min-h-screen relative overflow-hidden"
    >
      {/* Photo watermark */}
      <PhotoWatermark index={10} position="bottom-left" opacity={0.1} />
      {/* Ambient glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 w-[80rem] h-[40rem] opacity-[0.06] blur-3xl"
        style={{
          background:
            "radial-gradient(ellipse at top, #DE9E48 0%, transparent 60%)",
        }}
      />

      <div className="max-w-5xl mx-auto px-6 md:px-10 relative">
        {/* Back link */}
        <div className="flex flex-wrap items-center gap-3 mb-10">
          <Link
            to="/about"
            data-testid="directions-back-link"
            className="inline-flex items-center gap-2 font-overline text-[0.7rem] tracking-[0.22em] text-stone-400 hover:text-[#DE9E48] transition-colors"
          >
            <ArrowLeft size={14} />
            Back to About
          </Link>
        </div>

        {/* Page hero */}
        <header className="mb-16 md:mb-24">
          <p className="font-overline text-[0.7rem] tracking-[0.3em] text-[#DE9E48] mb-5">
            — Plan Your Visit
          </p>
          <h1 className="font-display text-5xl md:text-7xl lg:text-8xl leading-[0.92] tracking-tighter text-white mb-8 max-w-4xl">
            Directions &amp;{" "}
            <span className="italic text-[#DE9E48]">Parking.</span>
          </h1>
          <p className="font-body text-lg md:text-xl leading-relaxed text-stone-300 max-w-3xl">
            Encore Theatre &amp; Gallery is conveniently located in the heart
            of downtown Birmingham. Whether you're coming from across town or
            traveling in for a performance, we're easily accessible from
            I-20/59 and I-65 — with parking and transit options right at our
            doorstep.
          </p>

          {/* Address + CTA row */}
          <div className="mt-12 grid md:grid-cols-[1.2fr_1fr] gap-4">
            <div className="border border-[#DE9E48]/25 bg-[#372C2E]/60 backdrop-blur-sm p-6 md:p-8">
              <p className="font-overline text-[0.62rem] tracking-[0.28em] text-[#DE9E48] mb-3 inline-flex items-center gap-2">
                <MapPin size={12} />
                The Venue
              </p>
              <p className="font-display text-2xl md:text-3xl text-white leading-tight tracking-tight">
                Encore Theatre &amp; Gallery
              </p>
              <p className="font-body text-stone-300 mt-3 text-base md:text-lg">
                {ADDRESS_LINE_1}
                <br />
                {ADDRESS_LINE_2}
              </p>
            </div>
            <a
              data-testid="directions-open-maps"
              href={MAPS_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center justify-between gap-4 bg-[#7A431D] hover:bg-[#5C331A] text-white p-6 md:p-8 transition-all duration-300 hover:-translate-y-0.5"
            >
              <div>
                <p className="font-overline text-[0.62rem] tracking-[0.28em] text-white/70 mb-2">
                  — Turn-by-Turn
                </p>
                <p className="font-display text-2xl md:text-3xl leading-tight tracking-tight">
                  Open in Maps
                </p>
                <p className="font-body text-sm text-white/80 mt-2">
                  Launches Google Maps with route from your location.
                </p>
              </div>
              <Navigation
                size={28}
                strokeWidth={1.75}
                className="flex-shrink-0 group-hover:translate-x-1 transition-transform"
              />
            </a>
          </div>
        </header>

        {/* Embedded map */}
        <section className="mb-24 md:mb-32">
          <div className="relative border border-[#DE9E48]/25 overflow-hidden">
            {/* Top bronze accent bar */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-[#DE9E48]/20 bg-stone-950/40">
              <span className="font-overline text-[0.65rem] tracking-[0.22em] text-[#DE9E48]">
                — Interactive Map
              </span>
              <a
                data-testid="directions-map-fullscreen"
                href={`https://www.google.com/maps?q=${MAPS_QUERY}`}
                target="_blank"
                rel="noopener noreferrer"
                className="font-overline text-[0.6rem] tracking-[0.22em] text-stone-400 hover:text-[#DE9E48] inline-flex items-center gap-1.5 transition-colors"
              >
                Full screen <ExternalLink size={11} />
              </a>
            </div>
            <iframe
              data-testid="directions-map-iframe"
              title="Encore Theatre & Gallery — Map"
              src={MAPS_EMBED}
              width="100%"
              height="440"
              style={{ border: 0, display: "block", filter: "grayscale(20%) contrast(0.95)" }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </section>

        {/* Getting Here — Route Directory */}
        <section className="mb-24 md:mb-32">
          <SectionHeading
            eyebrow="Getting Here"
            title="Routes by Origin."
            icon={Compass}
          />
          <p className="font-body text-stone-300 leading-relaxed max-w-3xl -mt-6 mb-10 md:mb-12">
            Tap any origin to expand step-by-step driving directions. Once you
            reach downtown, watch the overhead signage carefully — exits come
            up quickly.
          </p>
          <div className="space-y-4">
            {ROUTES.map((r, i) => (
              <RouteCard key={r.id} route={r} defaultOpen={i === 0} />
            ))}
          </div>
        </section>

        {/* Parking */}
        <section className="mb-24 md:mb-32">
          <SectionHeading
            eyebrow="Where to Park"
            title="Parking."
            icon={ParkingCircle}
          />

          {/* Featured: The Lot */}
          <figure className="relative mb-10 group overflow-hidden border border-[#DE9E48]/25 bg-stone-950">
            {/* Bronze corner accents */}
            <span aria-hidden className="pointer-events-none absolute top-0 left-0 h-[2px] w-24 bg-[#DE9E48] z-20" />
            <span aria-hidden className="pointer-events-none absolute top-0 left-0 w-[2px] h-16 bg-[#DE9E48] z-20" />
            <span aria-hidden className="pointer-events-none absolute bottom-0 right-0 h-[2px] w-24 bg-[#DE9E48] z-20" />
            <span aria-hidden className="pointer-events-none absolute bottom-0 right-0 w-[2px] h-16 bg-[#DE9E48] z-20" />

            <div className="relative aspect-[21/9] md:aspect-[24/9] overflow-hidden">
              <img
                data-testid="parking-lot-photo"
                src="https://customer-assets-jt897jd0.emergentagent.net/job_encore-restore/artifacts/v4ydud8l_D17A3729-821B-46AA-8645-D30A2996DAD2.jpg"
                alt="The public surface parking lot directly across 2nd Avenue North from Encore Theatre & Gallery, framed by a mature shade tree."
                className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.04]"
                loading="lazy"
              />
              {/* Vertical bronze gradient wash */}
              <div
                aria-hidden
                className="absolute inset-0 bg-gradient-to-t from-[#211a1c] via-[#211a1c]/60 to-transparent"
              />
              {/* Left copy panel */}
              <div className="absolute inset-x-0 bottom-0 px-6 md:px-10 pb-8 md:pb-10">
                <div className="max-w-2xl">
                  <p className="font-overline text-[0.62rem] tracking-[0.3em] text-[#DE9E48] mb-3 inline-flex items-center gap-2">
                    <MapPin size={12} strokeWidth={2} />
                    Directly Across the Street
                  </p>
                  <h3 className="font-display text-3xl md:text-5xl leading-[1.02] tracking-tighter text-white mb-3">
                    The Lot on <span className="italic text-[#DE9E48]">2nd Ave.</span>
                  </h3>
                  <p className="font-body text-stone-200 text-base md:text-lg leading-relaxed max-w-xl">
                    A wide public surface lot sits just across from our front
                    doors — shaded, easy in-and-out, and steps from the marquee.
                  </p>
                </div>
              </div>
              {/* Top-right chip */}
              <div className="absolute top-5 right-5 md:top-6 md:right-6 flex items-center gap-2 px-3.5 py-1.5 bg-[#211a1c]/85 backdrop-blur-sm border border-[#DE9E48]/40">
                <ParkingCircle size={12} strokeWidth={2} className="text-[#DE9E48]" />
                <span className="font-overline text-[0.6rem] tracking-[0.24em] text-white">
                  Free evenings &amp; weekends
                </span>
              </div>
            </div>
            <figcaption className="sr-only">
              The public surface parking lot across 2nd Avenue N from Encore
              Theatre &amp; Gallery.
            </figcaption>
          </figure>

          <ol className="space-y-5">
            <ParkingStep
              n={1}
              title="Street Parking on 2nd Avenue N"
              meta="Free after 5 pm · Free weekends"
            >
              <p>
                Metered spaces line 2nd Avenue North directly in front of the
                venue. Meters are enforced 8 am – 5 pm on weekdays and are free
                on evenings and weekends — perfect for most performances.
              </p>
              <p className="text-stone-400 text-sm">
                Tip: The Park Mobile app makes daytime metered parking a breeze
                if you arrive early for a matinee.
              </p>
            </ParkingStep>

            <ParkingStep
              n={2}
              title="Surface Lot Across the Street"
              meta="Typically free · Occasional event fee"
            >
              <p>
                The lot pictured above sits directly across 2nd Avenue N from
                the theatre. It's normally open with no charge in the evenings.
                During select downtown events an attendant may collect a small
                fee — signage will be posted at the entrance.
              </p>
            </ParkingStep>

            <ParkingStep
              n={3}
              title="Nearby Decks &amp; Overflow"
              meta="Under $10 typical"
            >
              <p>
                Additional public decks are within a 2–3 block walk along 3rd
                Avenue N and 18th Street — a good backup on high-demand nights.
              </p>
            </ParkingStep>
          </ol>

          {/* Arrival tips */}
          <div className="mt-10 space-y-4">
            <div className="border-l-2 border-[#DE9E48] bg-[#372C2E]/50 pl-6 py-5 flex items-start gap-4">
              <Clock
                size={18}
                strokeWidth={1.75}
                className="text-[#DE9E48] flex-shrink-0 mt-0.5"
              />
              <p className="font-body italic text-stone-200 leading-relaxed">
                Please plan to arrive 20–30 minutes before curtain. This gives
                you time to navigate downtown, park, check in, and get settled
                before the performance begins. House opens 15 minutes prior to
                showtime; late seating is at the discretion of house management.
              </p>
            </div>
            <div className="border-l-2 border-[#DE9E48]/60 bg-[#372C2E]/40 pl-6 py-5 flex items-start gap-4">
              <Info
                size={18}
                strokeWidth={1.75}
                className="text-[#DE9E48] flex-shrink-0 mt-0.5"
              />
              <p className="font-body text-stone-300 leading-relaxed">
                Downtown Birmingham occasionally has street closures,
                construction, festivals, or special events — please allow a
                little extra travel time when needed. For the most accurate
                real-time route, enter our full address into your GPS or
                navigation app before you leave.
              </p>
            </div>
          </div>
        </section>

        {/* Public Transit */}
        <section className="mb-24 md:mb-32">
          <SectionHeading
            eyebrow="Public Transit"
            title="Transit &amp; Rideshare."
            icon={Bus}
          />
          <div className="grid md:grid-cols-2 gap-5">
            <InfoCard icon={Bus} title="MAX Transit (BJCTA)" tone="highlight">
              <p>
                Birmingham's regional bus network connects the theatre to
                neighborhoods across Jefferson County via fixed routes, the
                Birmingham Xpress rapid line, and on-demand microtransit.
                Central Station is roughly a five block walk.
              </p>
              <a
                data-testid="directions-max-transit"
                href="https://maxtransit.org/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 font-overline text-[0.65rem] tracking-[0.22em] text-[#DE9E48] hover:text-white transition-colors mt-2"
              >
                Plan a Trip on MAX <ExternalLink size={12} />
              </a>
            </InfoCard>
            <InfoCard icon={Car} title="Rideshare Drop-Off">
              <p>
                Uber and Lyft drop off curbside on 2nd Avenue N. For pickup
                after the show, request from the front doors — 2nd Ave is a
                one-way running east.
              </p>
            </InfoCard>
          </div>
        </section>

        {/* Accessibility */}
        <section className="mb-24 md:mb-32">
          <SectionHeading
            eyebrow="Accessibility"
            title="Arriving with Ease."
            icon={Accessibility}
          />
          <div className="grid md:grid-cols-3 gap-5">
            <InfoCard icon={Accessibility} title="Wheelchair Access">
              <p>
                The lobby and house are step-free from street level via the
                main entrance. Accessible seating is available — please note
                any needs when booking so we can hold the right seat for you.
              </p>
            </InfoCard>
            <InfoCard icon={ParkingCircle} title="Accessible Parking">
              <p>
                Designated accessible spaces are available along 2nd Avenue N
                and in the lot across the street. Curb cuts run the full
                length of the block.
              </p>
            </InfoCard>
            <InfoCard icon={Info} title="Assistive Requests">
              <p>
                Need large-print programs, hearing assistance, or advance
                lobby entry? Reach out via our{" "}
                <Link
                  to="/contact"
                  className="text-[#DE9E48] hover:text-white underline decoration-dotted underline-offset-4 transition-colors"
                >
                  contact page
                </Link>{" "}
                and we'll get you set.
              </p>
            </InfoCard>
          </div>
        </section>

        {/* Nearby / Explore */}
        <section className="mb-16">
          <SectionHeading
            eyebrow="Around the Theatre"
            title="Make a Night of It."
            icon={Utensils}
          />
          <div className="grid md:grid-cols-2 gap-5">
            <a
              data-testid="directions-cvb-link"
              href="https://www.birminghamal.org/travel/"
              target="_blank"
              rel="noopener noreferrer"
              className="group relative border border-white/8 hover:border-[#DE9E48]/40 bg-[#3A2C25]/40 hover:bg-[#3A2C25]/70 backdrop-blur-sm p-6 md:p-8 transition-all duration-500"
            >
              <span
                aria-hidden
                className="pointer-events-none absolute top-0 left-0 h-[2px] w-8 bg-[#DE9E48] transition-all duration-500 group-hover:w-24"
              />
              <p className="font-overline text-[0.62rem] tracking-[0.28em] text-[#DE9E48] mb-3">
                — Plan Your Trip
              </p>
              <h3 className="font-display text-2xl md:text-3xl text-white tracking-tight mb-3">
                Greater Birmingham CVB
              </h3>
              <p className="font-body text-[0.95rem] leading-relaxed text-stone-300 mb-4">
                Hotels, itineraries, and the best of Birmingham for visitors
                arriving from out of town.
              </p>
              <span className="inline-flex items-center gap-2 font-overline text-[0.65rem] tracking-[0.22em] text-[#DE9E48]">
                Visit birminghamal.org <ExternalLink size={12} />
              </span>
            </a>

            <div className="group relative border border-white/8 bg-[#3A2C25]/40 backdrop-blur-sm p-6 md:p-8">
              <span
                aria-hidden
                className="pointer-events-none absolute top-0 left-0 h-[2px] w-8 bg-[#DE9E48]"
              />
              <p className="font-overline text-[0.62rem] tracking-[0.28em] text-[#DE9E48] mb-3">
                — Pre-Show &amp; After
              </p>
              <h3 className="font-display text-2xl md:text-3xl text-white tracking-tight mb-3">
                Downtown Dining
              </h3>
              <p className="font-body text-[0.95rem] leading-relaxed text-stone-300">
                Second Avenue North and the surrounding Loft District are
                lined with restaurants for every taste and budget — from
                classic Southern to global small plates, all within a short
                walk.
              </p>
            </div>
          </div>
        </section>

        {/* Closing */}
        <div className="mt-24 md:mt-32 border-t border-[#DE9E48]/15 pt-12 flex flex-wrap items-center justify-between gap-6">
          <div>
            <p className="font-overline text-[0.62rem] tracking-[0.28em] text-[#DE9E48] mb-2 inline-flex items-center gap-2">
              <MapPin size={12} strokeWidth={2} />
              Safe travels
            </p>
            <p className="font-display italic text-2xl md:text-3xl leading-tight text-white max-w-xl">
              We'll see you at Encore.
            </p>
          </div>
          <Link
            to="/contact"
            data-testid="directions-contact-cta"
            className="inline-flex items-center gap-3 px-7 py-3.5 bg-[#7A431D] hover:bg-[#5C331A] text-white font-overline text-xs tracking-[0.22em] uppercase rounded-full transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_10px_30px_-10px_rgba(222,158,72,0.5)]"
          >
            Questions? Contact Us
          </Link>
        </div>
      </div>
    </div>
  );
}
